import type { GradingReferenceWrapper } from "@repo/ui/structuredContent/gradingReferenceContext";
import { useEffect, useMemo, useRef, type FC, type ReactNode } from "react";
import { AnalyticsProvider } from "../analytics/analyticsContext/analyticsProvider.js";
import type { AnalyticsProviderValue } from "../analytics/analyticsContext/scopes.js";
import { useAnalyticsContexts } from "../analytics/analyticsContext/useAnalyticsContexts.js";
import { StructuredContentReferenceProviders } from "../structuredContent/structuredContentReferenceProviders.js";
import {
  useCoachingAnalytics,
  type CoachingGoalViewVariant,
} from "./coachingAnalytics.js";
import { GradingReferenceLinkAdapter } from "./gradingReferenceLinkAdapter.js";

interface CoachingAnalyticsScopeProviderProps {
  value: AnalyticsProviderValue<"coaching">;
  children: ReactNode;
}

/**
 * Publishes one coaching scope to engagement events and grading-reference
 * links, preventing view and quote events on the same content from drifting.
 * Also installs the shared reference providers, so coaching content links its
 * teammates, customers and playbook feedback.
 */
export const CoachingAnalyticsScopeProvider: FC<
  CoachingAnalyticsScopeProviderProps
> = (props) => {
  const { value, children } = props;
  const parent = useAnalyticsContexts();
  const resolvedValue = typeof value === "function" ? value(parent) : value;
  const { teammateId, playbookId, periodId, criterionId } = resolvedValue;
  const stableValue = useMemo(
    () => ({ teammateId, playbookId, periodId, criterionId }),
    [criterionId, periodId, playbookId, teammateId],
  );

  return (
    <AnalyticsProvider scope="coaching" value={stableValue}>
      <StructuredContentReferenceProviders
        gradingReferenceWrapper={CoachingGradingReferenceLinkAdapter}
      >
        {children}
      </StructuredContentReferenceProviders>
    </AnalyticsProvider>
  );
};

interface CoachingGoalViewTrackerProps {
  variant: CoachingGoalViewVariant;
  /** False while the branch still renders a loading shell. */
  isReady?: boolean;
  children: ReactNode;
}

/**
 * Emits when the ready view identity changes without repeating for rerenders
 * of the same active view. Revisiting an earlier identity is a new impression.
 */
export const CoachingGoalViewTracker: FC<CoachingGoalViewTrackerProps> = (
  props,
) => {
  const { variant, isReady = true, children } = props;
  const scope = useAnalyticsContexts("coaching");

  const { teammateId, playbookId, periodId } = scope;

  // `criterionId` is deliberately excluded from the identity: it can resolve
  // after the view is already on screen, and re-keying on it would emit a
  // second view for the same goal. The capture hook reads the current coaching
  // provider directly. Pinned by "captures one ready view in Strict Mode
  // without recapturing criterion updates" in the scope tests.
  const identity = JSON.stringify([teammateId, playbookId, periodId, variant]);
  const capturedIdentityRef = useRef<string | null>(null);

  const { captureGoalView } = useCoachingAnalytics();
  useEffect(() => {
    if (!isReady || capturedIdentityRef.current === identity) {
      return;
    }

    captureGoalView({ variant });
    capturedIdentityRef.current = identity;
  }, [captureGoalView, identity, isReady, variant]);

  return children;
};

const CoachingGradingReferenceLinkAdapter: GradingReferenceWrapper = (
  props,
) => {
  const { reference, className, children } = props;
  const { captureQuoteOpen } = useCoachingAnalytics();

  return (
    <GradingReferenceLinkAdapter
      reference={reference}
      className={className}
      onGradingOpen={(gradingId, referenceTarget) => {
        captureQuoteOpen({ referenceTarget, gradingId });
      }}
    >
      {children}
    </GradingReferenceLinkAdapter>
  );
};
